import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || '/api';

const api = axios.create({
    baseURL: API_URL,
    headers: { 'Content-Type': 'application/json' }
});

api.interceptors.request.use((config) => {
    const token = localStorage.getItem('token');
    if (token) config.headers.Authorization = 'Bearer ' + token;
    return config;
});

api.interceptors.response.use(
    (response) => response,
    (error) => {
        if (error.response?.status === 401) {
            localStorage.removeItem('token');
            localStorage.removeItem('user');
            window.location.reload();
        }
        return Promise.reject(error);
    }
);

// Identité visuelle lue par la page de connexion, donc SANS jeton (liste blanche
// de middleware/auth.js) : ne renvoie que le nom de l'application et le logo.
export const brandingAPI = {
    get: () => api.get('/branding')
};

export const authAPI = {
    login: (email, password) => api.post('/auth/login', { email, password }),
    register: (data) => api.post('/auth/register', data),
    me: () => api.get('/auth/me'),
    savePreferences: (preferences) => api.patch('/auth/me/preferences', { preferences })
};

export const calendrierAPI = {
    getActif: () => api.get('/calendrier/actif'),
    getAll: () => api.get('/calendrier/toutes'),
    // Avec l'état du calendrier de chaque année (jours fériés, vacances) — onglet
    // Calendriers uniquement : le sélecteur d'année n'a pas besoin de ces comptes.
    getAllAvecStats: () => api.get('/calendrier/toutes?avecStats=true'),
    getAllVisible: () => api.get('/calendrier/toutes?visibles=true'),
    getByAnnee: (anneeId) => api.get(`/calendrier/annee/${anneeId}`),
    // Créer une année universitaire — super-administrateur, onglet Calendriers.
    // L'année naît INACTIVE : « Rendre active » reste un geste distinct.
    // zone : 'A' | 'B' | 'C' | 'NC' (aucune vacance générée)
    creerAnnee: (anneeDebut, zone) => api.post('/calendrier/annees', { anneeDebut, zone }),
    // Recalculer jours fériés et vacances d'une année (les dates officielles de
    // l'année suivante ne sont publiées qu'au printemps). N'change pas l'activation.
    regenererAnnee: (anneeId, zone) => api.post(`/calendrier/annees/${anneeId}/regenerer`, { zone }),
    toggleVisibilite: (anneeId) => api.patch(`/calendrier/annee/${anneeId}/visibilite`),
    activerAnnee: (anneeId) => api.patch(`/calendrier/annee/${anneeId}/activer`),
    deleteAnnee: (anneeId) => api.delete(`/calendrier/annee/${anneeId}`)
};

export const departementsAPI = {
    getAll: () => api.get('/departements'),
    getById: (id) => api.get('/departements/' + id),
    getPlanningConfig: (id) => api.get('/departements/' + id + '/planning-config'),
    // D28 — l'en-tête des documents (lecture par les modules : tout compte du département)
    getEntete: (id) => api.get('/departements/' + id + '/entete'),
    getAccueilStats: (id) => api.get('/departements/' + id + '/accueil-stats'),
    getPlanningStats: (id, anneeId) => api.get('/departements/' + id + '/planning-stats?annee_id=' + anneeId),
    create: (data) => api.post('/departements', data),
    update: (id, data) => api.put('/departements/' + id, data),
    // Lot CO-b : suppression DÉFINITIVE d'un département vide (super-admin), confirmée par son code
    inventaireSuppression: (id) => api.get('/departements/' + id + '/suppression'),
    supprimerDefinitivement: (id, code) => api.delete('/departements/' + id + '?confirmation=' + encodeURIComponent(code)),
    uploadLogo: (id, formData) => api.post('/departements/' + id + '/logo', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
    }),
    deleteLogo: (id) => api.delete('/departements/' + id + '/logo')
};

export const niveauxAPI = {
    getAll: () => api.get('/niveaux'),
    getSousNiveaux: (deptId) => api.get('/niveaux/sous-niveaux/' + deptId),
    createSousNiveau: (data) => api.post('/niveaux/sous-niveaux', data),
    updateSousNiveau: (id, data) => api.put('/niveaux/sous-niveaux/' + id, data),
    deleteSousNiveau: (id) => api.delete('/niveaux/sous-niveaux/' + id),
    // Garde-fou D-a (lot 5.4) : ce que la sauvegarde du Paramétrage s'apprête à
    // détruire. Interrogé AVANT d'écrire, jamais après.
    impactSuppression: (ids) => api.post('/niveaux/sous-niveaux/impact-suppression', { ids }),
    deleteAllSousNiveaux: (deptId) => api.delete('/niveaux/sous-niveaux/dept/' + deptId)
};

export const evenementsAPI = {
    // ── LE RÉFÉRENTIEL : LES CATÉGORIES (lot 5.4 ter) ────────────────────────
    // Par département et INDÉPENDANTES DE L'ANNÉE : aucune de ces routes ne prend
    // d'année, c'est tout l'objet du lot.
    //
    // `departementId` est passé en paramètre d'URL sur les routes qui n'ont qu'un
    // identifiant nu : le contrôle de droits déduit le département de la requête,
    // et sans lui un utilisateur rattaché à PLUSIEURS départements se verrait
    // refuser l'accès faute de savoir lequel viser. Ce paramètre n'accorde aucun
    // droit — le serveur revérifie le département RÉEL de la ligne visée.
    getCategories: (deptId) => api.get('/evenements/categories/' + deptId),
    createCategorie: (data) => api.post('/evenements/categories', data),
    updateCategorie: (id, data) => api.put('/evenements/categories/' + id, data),
    impactCategorie: (id, deptId) =>
        api.get('/evenements/categories/' + id + '/impact', { params: { departementId: deptId } }),
    deleteCategorie: (id, deptId) =>
        api.delete('/evenements/categories/' + id, { params: { departementId: deptId } }),

    // ── LES ÉVÉNEMENTS TYPES ─────────────────────────────────────────────────
    getConfig: (deptId, params) => api.get('/evenements/config/' + deptId, { params }),
    createConfig: (data) => api.post('/evenements/config', data),
    updateConfig: (id, data) => api.put('/evenements/config/' + id, data),
    deleteConfig: (id) => api.delete('/evenements/config/' + id),
    // `importPreviousYear` a été retirée au lot 5.4 ter avec la carte du Paramétrage
    // qu'elle servait. Le panneau du Calendrier fait la même chose en mieux : il
    // permet de CHOISIR ce qu'on reprend, là où celle-ci refusait dès que l'année
    // contenait un événement. Le remplacement était en place avant le retrait.
    // Image de l'événement type (lot 5.4 bis) — `data` est un FormData, comme pour
    // le logo de département. L'en-tête multipart est posé par le navigateur.
    uploadLogo: (id, data) => api.post('/evenements/config/' + id + '/logo', data),
    // `getDates` a été retirée au lot 5.4 bis : elle appelait
    // `GET /evenements/dates/:anneeId`, route qui N'EXISTE PAS côté serveur, et
    // n'avait aucun appelant. Elle n'a donc jamais pu fonctionner — ce n'est pas
    // une suppression avant remplacement, c'est le retrait d'un appel vers un 404.
    createDate: (data) => api.post('/evenements/dates', data),
    updateDate: (id, data) => api.put('/evenements/dates/' + id, data),
    deleteDate: (id) => api.delete('/evenements/dates/' + id)
};

export const periodesAPI = {
    getAll: (anneeId, params) => api.get('/periodes/' + anneeId, { params }),
    create: (data) => api.post('/periodes', data),
    createBatch: (data) => api.post('/periodes/batch', data),
    update: (id, data) => api.put('/periodes/' + id, data),
    delete: (id) => api.delete('/periodes/' + id),
    clear: (anneeId, params) => api.delete('/periodes/clear/' + anneeId, { params })
};

/** Périmètre des Signatures : un département (id ou « d<id> ») ou une composante (« c<id> »). */
const parametrePerimetre = (p) => {
    if (p === null || p === undefined || p === '') return '';
    const s = String(p);
    if (s.startsWith('c')) return '?composanteId=' + s.slice(1);
    return '?departementId=' + (s.startsWith('d') ? s.slice(1) : s);
};

export const adminAPI = {
    // `deptId` = périmètre affiché dans le panneau (sélecteur de département de la
    // topbar). Le serveur borne de toute façon la réponse au périmètre du compte.
    getUsers: (deptId) => api.get('/admin/users' + (deptId ? '?departementId=' + deptId : '')),
    createUser: (data) => api.post('/admin/users', data),
    updateUser: (id, data) => api.put('/admin/users/' + id, data),
    // Lot INV-b (v83) : l'accès au compte — invitation, lien à copier (montré une fois), lien de réinitialisation
    inviterUser: (id, departementId) => api.post('/admin/users/' + id + '/invitation', { departementId }),
    lienUser: (id) => api.post('/admin/users/' + id + '/lien', {}),
    reinitialiserUser: (id, departementId) => api.post('/admin/users/' + id + '/reinitialisation', { departementId }),
    // Lot INV-c : « Inviter les N comptes », son essai, le journal des accès
    inviterPlusieurs: (ids, departementId) => api.post('/admin/users/invitations', { ids, departementId }),
    essaiInvitation: (departementId) => api.post('/admin/users/invitations/essai', { departementId }),
    journalAcces: (jours, departementId) => api.get('/admin/journal-acces', { params: { jours, departementId: departementId || undefined } }),
    deleteUser: (id) => api.delete('/admin/users/' + id),
    reactivateUser: (id) => api.post('/admin/users/' + id + '/reactivate'),
    getDepartements: () => api.get('/admin/departements'),
    getRoles: () => api.get('/admin/roles'),
    getProfils: (deptId) => api.get('/admin/profils' + (deptId ? '?departementId=' + deptId : '')),
    getEnseignants: (deptId) => api.get('/admin/enseignants' + (deptId ? '?departementId=' + deptId : '')),
    // Nommer / retirer les administrateurs d'un département (liste complète voulue)
    setDeptAdmins: (deptId, userIds) => api.put('/admin/departements/' + deptId + '/admins', { userIds }),
    // R2 (lot CO-a) : l'alternative de l'administrateur qui ne couvre pas toute la personne
    retirerDuDepartement: (userId, deptId) => api.delete('/admin/users/' + userId + '/departements/' + deptId),

    // ---- Composantes (lot CO-a) ----
    getComposantes: () => api.get('/admin/composantes'),
    createComposante: (data) => api.post('/admin/composantes', data),
    updateComposante: (id, data) => api.put('/admin/composantes/' + id, data),
    deleteComposante: (id) => api.delete('/admin/composantes/' + id),
    setComposanteAdmins: (id, userIds) => api.put('/admin/composantes/' + id + '/admins', { userIds }),
    // Lot CO-b : candidats « premier administrateur » d'un nouveau département de cette composante
    getComptesComposante: (id) => api.get('/admin/composantes/' + id + '/comptes'),

    // ---- Profils & droits (0.C.3) ----
    createProfil: (data) => api.post('/admin/profils', data),
    // `permissions` = { module_key: niveau } — partiel accepté (la matrice envoie une cellule)
    setProfilPermissions: (id, permissions) => api.put('/admin/profils/' + id + '/permissions', { permissions }),
    deleteProfil: (id) => api.delete('/admin/profils/' + id),
    // Droits résolus d'un compte avec la SOURCE de chaque niveau (profil / exception / défaut)
    getUserDroits: (id, deptId) => api.get('/admin/users/' + id + '/droits?departementId=' + deptId),
    setUserProfil: (id, deptId, profilId) => api.put('/admin/users/' + id + '/profil', { departementId: deptId, profilId }),
    setUserOverride: (id, deptId, moduleKey, niveau) =>
        api.put('/admin/users/' + id + '/overrides', { departementId: deptId, moduleKey, niveau }),

    // ---- Clés d'API et connexions (0.C.6 / 0.C.7) ----
    getApiKeys: () => api.get('/admin/api-keys'),
    // La réponse contient `cleComplete` : c'est la SEULE fois où elle est lisible,
    // seule son empreinte est stockée.
    createApiKey: (data) => api.post('/admin/api-keys', data),
    revokeApiKey: (id) => api.delete('/admin/api-keys/' + id),
    getApiJournal: (limite) => api.get('/admin/api-keys/journal' + (limite ? '?limite=' + limite : '')),
    // Configuration SSO en lecture (le secret client n'est jamais renvoyé)
    getSso: () => api.get('/admin/sso'),

    // ---- Signatures et délégations (0.C.5) ----
    // Aucune route ne renvoie l'image : seulement le fait qu'elle existe.
    // `perimetre` : un id de département, ou « c<id> » pour toute une composante (S2, lot CO-a)
    getSignatures: (perimetre) => api.get('/admin/signatures' + parametrePerimetre(perimetre)),
    // `formData` porte les fichiers ET le mot de passe (ré-authentification).
    // L'en-tête est forcé comme pour le logo : l'instance axios impose
    // `application/json` par défaut, ce qui empêcherait la lecture du multipart.
    deposerSignature: (id, formData) => api.post('/admin/users/' + id + '/signature', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
    }),
    retirerSignature: (id, type) => api.delete('/admin/users/' + id + '/signature?type=' + type),
    // ---- Configuration globale (0.C.8) ----
    getSettings: () => api.get('/admin/settings'),
    // Suivi de l'occupation, lecture seule (lot 5.3) : version de migration réelle,
    // poids de la base, et les 5 tables les plus lourdes. Remplace le « v32 » écrit
    // en dur dans la carte « À propos ».
    getOccupation: () => api.get('/admin/occupation'),
    // Lot D.1 — Administration → Documents (super-administrateur).
    getDocuments: () => api.get('/admin/documents'),
    saveDureesDocuments: (durees) => api.put('/admin/documents/familles', { durees }),
    purgerDocuments: (attendu) => api.post('/admin/documents/purge', { attendu }),
    saveSettings: (data) => api.put('/admin/settings', data),
    // Lot 3.S-a — l'envoi des courriels (jamais le mot de passe dans les réponses)
    getEtatsCourriel: () => api.get('/admin/courriel/etats'),
    getCourriel: (deptId) => api.get(`/admin/departements/${deptId}/courriel`),
    saveCourriel: (deptId, data) => api.put(`/admin/departements/${deptId}/courriel`, data),
    testerCourriel: (deptId) => api.post(`/admin/departements/${deptId}/courriel/test`),
    // Lot ADM-E (D28) — l'en-tête des documents, par département
    getEtatsEntete: () => api.get('/admin/entete/etats'),
    getEntete: (deptId) => api.get(`/admin/departements/${deptId}/entete`),
    saveEntete: (deptId, data) => api.put(`/admin/departements/${deptId}/entete`, data),
    uploadLogoUniversite: (formData) => api.post('/admin/settings/logo', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
    }),
    deleteLogoUniversite: () => api.delete('/admin/settings/logo'),

    getDelegations: (perimetre) => api.get('/admin/delegations' + parametrePerimetre(perimetre)),
    createDelegation: (data) => api.post('/admin/delegations', data),
    revoquerDelegation: (id) => api.delete('/admin/delegations/' + id)
};

export const exportAPI = {
    // Les RÉGLAGES COURANTS — un seul jeu par planning, « l'écran comme je l'ai laissé ».
    getPreferences: (deptId, anneeId) => api.get('/export/preferences/' + deptId + '/' + anneeId),
    savePreferences: (data) => api.put('/export/preferences', data),
    getConfig: (deptId, anneeId) => api.get('/export/config/' + deptId + '/' + anneeId),
    saveConfig: (data) => api.put('/export/config', data),

    // Les MODÈLES ENREGISTRÉS (lot 5.6 bis) — autant qu'on veut, nommés, partagés
    // au département. Ils contiennent la CONFIGURATION, jamais le PDF.
    getModeles: (deptId, anneeId, type = 'calendrier') =>
        api.get('/export/modeles/' + deptId + '/' + anneeId + '?type=' + type),
    creerModele: (data) => api.post('/export/modeles', data),
    remplacerModele: (id, data) => api.put('/export/modeles/' + id, data),
    // v94 (lot PA.2) : le modèle devient (ou cesse d'être) une vignette de l'accueil
    basculerAccueil: (id, departementId, surAccueil) =>
        api.patch('/export/modeles/' + id + '/accueil', { departementId, surAccueil }),
    supprimerModele: (id) => api.delete('/export/modeles/' + id)
};

export const typesJourneesAPI = {
    getAll: (deptId) => api.get('/types-journees/' + deptId),
    save: (deptId, types) => api.post('/types-journees/' + deptId, { types })
};

export const niveauxConfigAPI = {
    getAll: (deptId) => api.get('/niveaux-config/' + deptId),
    save: (deptId, niveaux) => api.post('/niveaux-config/' + deptId, { niveaux })
};

// ============================================
// API ÉTUDIANTS
// ============================================

// ⚠️ Lot 5.1 (2026-08-08) — ces appels utilisaient `axios` BRUT et non l'instance
// `api` : aucun en-tête `Authorization` n'était envoyé, donc 401 systématique
// depuis 0.B.2 (jeton exigé sur tout `/api`). Conséquence visible dans le
// Planning : la pastille du nombre d'étudiants de chaque groupe, dans
// Paramétrage → Niveaux, groupes & heures, restait vide sans un mot.
// Les deux appels d'upload conservent leur en-tête `multipart/form-data`
// explicite : l'instance impose `application/json` par défaut, et l'oublier
// donnait « Aucun fichier reçu » (défaut rencontré au lot 5 du Bloc 0).
export const studentsAPI = {
    analyzeCSV: async (file, departementId, encoding = null) => {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('departementId', departementId);
        if (encoding) formData.append('encoding', encoding);
        return api.post('/students/analyze-csv', formData, {
            headers: { 'Content-Type': 'multipart/form-data' }
        });
    },
    import: async (file, options) => {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('departementId', options.departementId);
        if (options.sousNiveauId) formData.append('sousNiveauId', options.sousNiveauId);
        if (options.filtreGroupes) formData.append('filtreGroupes', JSON.stringify(options.filtreGroupes));
        if (options.excludedUids) formData.append('excludedUids', JSON.stringify(options.excludedUids));
        if (options.mode) formData.append('mode', options.mode);
        if (options.saveMapping !== undefined) formData.append('saveMapping', options.saveMapping);
        if (options.encoding) formData.append('encoding', options.encoding);
        return api.post('/students/import', formData, {
            headers: { 'Content-Type': 'multipart/form-data' }
        });
    },
    getBySousNiveau: async (sousNiveauId, page = 1, limit = 50, search = '') => {
        return api.get(`/students/sous-niveau/${sousNiveauId}`, {
            params: { page, limit, search }
        });
    },
    count: async (sousNiveauId) => {
        return api.get(`/students/count/${sousNiveauId}`);
    },
    delete: async (id) => {
        return api.delete(`/students/${id}`);
    },
    batchDelete: async (ids) => {
        return api.post('/students/batch/delete', { ids });
    },
    getMappings: async (departementId) => {
        return api.get(`/students/mappings/${departementId}`);
    },
    desaffectGroupe: async (departementId, groupePronote) => {
        return api.delete(`/students/desaffect/${departementId}/${encodeURIComponent(groupePronote)}`);
    },
    getModeles: async (departementId) => {
        return api.get(`/students/modeles/${departementId}`);
    },
    saveModele: async (data) => {
        return api.post('/students/modeles', data);
    },
    deleteModele: async (id) => {
        return api.delete(`/students/modeles/${id}`);
    }
};

// ============================================
// API MODULE ÉTUDIANTS (lot 1.1 — l'onglet Paramétrage)
//
// ⚠️ `studentsAPI` juste au-dessus est l'import PRONOTE du Planning annuel, pas ce
// module. Les deux routeurs coexistent le temps que le bouton d'import PRONOTE
// disparaisse (engagement écrit dans `setup/ParamConfiguration.js`).
//
// `departementId` accompagne CHAQUE appel : le contrôle de droits déduit de lui le
// département sur lequel vérifier la permission, et sans lui un compte rattaché à
// plusieurs départements reçoit un 403. Il n'accorde aucun droit — le serveur
// revérifie le département réel de la formation et de la ligne visées.
// ============================================

export const etudiantsAPI = {
    getParametrage: (deptId) => api.get('/etudiants/parametrage', { params: { departementId: deptId } }),
    // `reglages` est PARTIEL : chaque carte n'envoie que ce qu'elle porte. Le serveur
    // refuse le lot entier si l'un des réglages est invalide, plutôt que d'en écrire
    // la moitié.
    saveParametrage: (deptId, reglages) =>
        api.put('/etudiants/parametrage', { departementId: deptId, reglages }),

    // Saisie EN LOT : une formation, plusieurs couples code étape / version, chacun
    // avec son régime. `lignes` = [{ code_etape, version_etape, regime_inscription }].
    // La réponse porte le bilan ligne à ligne — `crees` et `refuses` — jamais un simple
    // succès global : une ligne en doublon doit pouvoir être nommée à l'écran.
    creerMappings: (deptId, sousNiveauId, lignes) =>
        api.post('/etudiants/mapping-apogee',
            { departementId: deptId, sous_niveau_id: sousNiveauId, lignes }),
    modifierMapping: (id, deptId, data) =>
        api.put('/etudiants/mapping-apogee/' + id, { departementId: deptId, ...data }),
    supprimerMapping: (id, deptId) =>
        api.delete('/etudiants/mapping-apogee/' + id, { params: { departementId: deptId } }),

    // ---- Lot E.1/E.2 (v97) : le mapping des GROUPES SCODOC — une valeur d'une partition, dans un niveau →
    // groupe + régime et/ou parcours. `data` = { niveau_id, partition, valeur, sous_niveau_id, regime_inscription,
    // parcours_code }. La création REMPLACE l'association de la même valeur (l'étape ② de l'import la corrige sur place).
    creerMappingScodoc: (deptId, data) =>
        api.post('/etudiants/mapping-scodoc', { departementId: deptId, ...data }),
    modifierMappingScodoc: (id, deptId, data) =>
        api.put('/etudiants/mapping-scodoc/' + id, { departementId: deptId, ...data }),
    supprimerMappingScodoc: (id, deptId) =>
        api.delete('/etudiants/mapping-scodoc/' + id, { params: { departementId: deptId } }),

    // ---- Lot E.3 : l'import SCODOC des ÉTUDIANTS (export d'admission) — `niveaux` = les formations choisies,
    // SCODOC ne dit pas la sienne. Même contrôle d'année que les autres imports.
    apercuImportScodocEtudiants: (deptId, anneeId, niveaux, lignes) =>
        api.post('/etudiants/import-scodoc-etudiants/apercu',
            { departementId: deptId, annee_scolaire_id: anneeId, niveaux, lignes }),
    importerScodocEtudiants: (deptId, anneeId, niveaux, lignes, fichierNom, mode = 'fichier') =>
        api.post('/etudiants/import-scodoc-etudiants',
            { departementId: deptId, annee_scolaire_id: anneeId, niveaux, lignes, fichier_nom: fichierNom, mode }),

    // ---- Import APOGEE (lot 1.2) ----
    // `lignes` ne porte QUE la liste blanche de `services/apogeeImport.js` : le fichier
    // est lu dans le navigateur, et les colonnes que le RGPD écarte ne quittent jamais
    // le poste. Mesuré : 147 étudiants font 63 ko, d'où le plafond de corps élargi sur
    // cette seule route dans `server.js`.
    //
    // `annee_scolaire_id` est envoyé pour être VÉRIFIÉ, pas pour être suivi : le
    // serveur relit l'année active et refuse (403) si elle a changé depuis l'ouverture
    // de l'écran.
    apercuImportApogee: (deptId, anneeId, lignes) =>
        api.post('/etudiants/import-apogee/apercu',
            { departementId: deptId, annee_scolaire_id: anneeId, lignes }),
    importerApogee: (deptId, anneeId, lignes, fichierNom, mode = 'fichier') =>
        api.post('/etudiants/import-apogee',
            { departementId: deptId, annee_scolaire_id: anneeId, lignes, fichier_nom: fichierNom, mode }),
    getImports: (deptId, source = 'apogee') =>
        api.get('/etudiants/imports', { params: { departementId: deptId, source } }),

    // ---- Onglet 2 : l'import SCODOC (lot 1.4) ----
    // Même forme que les deux appels d'APOGEE, et la même année : le serveur relit
    // l'année active et refuse (403) si l'écran en annonce une autre.
    // `semestre` n'est utile qu'au rang DE PROMOTION, qui change à chaque semestre.
    // Il part quand même toujours : le serveur ne l'exige que si le lot apporte un
    // champ qui en a besoin, et deux signatures d'appel seraient deux occasions
    // d'oublier de le transmettre.
    apercuImportScodoc: (deptId, anneeId, lignes, semestre = null) =>
        api.post('/etudiants/import-scodoc/apercu',
            { departementId: deptId, annee_scolaire_id: anneeId, lignes, semestre }),
    importerScodoc: (deptId, anneeId, lignes, fichierNom, mode = 'fichier', semestre = null) =>
        api.post('/etudiants/import-scodoc',
            { departementId: deptId, annee_scolaire_id: anneeId, lignes,
              fichier_nom: fichierNom, mode, semestre }),

    // ---- Désactivation / réactivation (lot 1.6, écart E9) ----
    // ⚠️ UNE ROUTE À PART, et pas un champ du PUT : désactiver SUPPRIME LA PHOTO,
    // et c'est irréversible. Glissé parmi les dix-sept champs de la fiche, ce
    // booléen aurait fait disparaître une image au détour d'un enregistrement
    // ordinaire. Le geste doit être demandé pour lui-même.
    changerActif: (deptId, id, actif) =>
        api.patch('/etudiants/' + id + '/actif', { departementId: deptId, actif }),

    // ---- Onglet 3 : les photos (lot 1.5) ----
    // ⚠️ LE DÉPARTEMENT PART EN PARAMÈTRE D'URL, pas dans le corps. C'est la seule
    // route du module qui envoie du `multipart`, et `requireModule` est monté au
    // niveau de l'application — donc AVANT que le corps ne soit analysé. À ce
    // moment-là `req.body` est vide, et le département serait indéterminé pour un
    // chef qui en a plusieurs. Côté serveur, `deduireDepartement` lit `req.query`
    // en PREMIER : c'est par là que ça passe.
    //
    // ⚠️ L'EN-TÊTE MULTIPART EST EXPLICITE, ET CE N'EST PAS FACULTATIF. L'instance
    // `api` impose `Content-Type: application/json` au niveau de l'instance (voir
    // sa création en tête de ce fichier) : sans cette surcharge, le corps part en
    // JSON, sans frontière, et `multer` ne voit AUCUN fichier — le serveur répond
    // « Aucune archive reçue » alors que l'archive est bien partie.
    //
    // C'est le même piège que `studentsAPI` avait rencontré au lot 5 du Bloc 0, et
    // il est déjà documenté au-dessus. Je l'ai repayé au lot 1.5 : trouvé par la
    // relecture à l'image, jamais par la compilation. Axios complète lui-même la
    // frontière quand le type est `multipart/form-data` et le corps un FormData.
    importerPhotos: (deptId, anneeId, fichier) => {
        const corps = new FormData();
        corps.append('archive', fichier);
        corps.append('annee_scolaire_id', String(anneeId));
        return api.post('/etudiants/import-photos', corps, {
            params: { departementId: deptId },
            headers: { 'Content-Type': 'multipart/form-data' }
        });
    },

    // 2026-10-22 (504 en production) : l'import tourne en TÂCHE DE FOND — `importerPhotos` rend `{ tache_id }`,
    // l'écran suit l'avancement ici jusqu'au bilan (ou au refus).
    etatImportPhotos: (deptId, tacheId) =>
        api.get(`/etudiants/import-photos/taches/${tacheId}`, { params: { departementId: deptId } }),

    // L'historique des imports de photos passe par `getImports(deptId, 'photos')` :
    // la route générique existe depuis le lot 1.2 et accepte déjà cette source
    // (écart E4 — UNE table d'historique filtrée sur `source`, et l'onglet Photos
    // est le troisième cas qui justifiait ce choix).

    // ---- Onglet 1 : la liste et la fiche (lot 1.3) ----
    // Les six filtres partent au SERVEUR et non au navigateur : il est le seul à
    // pouvoir compter juste (`total_annee`, l'effectif par formation) et le seul à
    // porter le périmètre. `filtres` est partiel — seules les clés renseignées sont
    // transmises, une clé vide vaut « pas de filtre ».
    //
    // ⚠️ Contrairement à l'import (écart E25), la lecture n'est PAS bornée à l'année
    // active : le filtre « Année » de la maquette n'aurait rien à filtrer sinon.
    getListe: (deptId, filtres = {}) => api.get('/etudiants', {
        params: {
            departementId: deptId,
            annee_scolaire_id: filtres.anneeId || undefined,
            sous_niveau_id: filtres.sousNiveauId || undefined,
            regime: filtres.regime || undefined,
            statut: filtres.statut || undefined,
            boursier: filtres.boursier || undefined,
            parcours: filtres.parcours || undefined,
            q: filtres.recherche || undefined
        }
    }),
    getFiche: (deptId, etudiantId, anneeId) => api.get('/etudiants/' + etudiantId, {
        params: { departementId: deptId, annee_scolaire_id: anneeId || undefined }
    }),

    // ---- Onglet 1 : l'écriture de la fiche (lot 1.3 bis) ----
    // `corps` porte les valeurs (`personne`, `inscription`), l'état complet des treize
    // cadenas (`champs_verrouilles`) et les DEUX horodatages lus à l'ouverture.
    //
    // ⚠️ Les horodatages ne sont pas décoratifs : le serveur refuse d'écrire (409) si
    // la ligne a bougé depuis, parce qu'un import a pu passer entre l'ouverture de la
    // fiche et le clic sur « Enregistrer ». Sans eux, la saisie effacerait en silence
    // ce que l'import vient d'écrire — le seul défaut de ce module que personne ne
    // pourrait constater après coup.
    // 2026-10-22 : ce que change une nouvelle formation / un nouveau régime, avant d'enregistrer (rien n'est écrit).
    consequencesFiche: (deptId, etudiantId, anneeId, { sous_niveau_id, regime_inscription }) =>
        api.get('/etudiants/' + etudiantId + '/consequences', {
            params: { departementId: deptId, annee_scolaire_id: anneeId || undefined, sous_niveau_id, regime_inscription }
        }),

    saveFiche: (deptId, etudiantId, anneeId, corps) =>
        api.put('/etudiants/' + etudiantId, {
            departementId: deptId, annee_scolaire_id: anneeId, ...corps
        })
};

// ============================================
// API PLANNING ANNUEL (routes propres au module)
// ============================================

export const planningAPI = {
    // Filtre « Détail par mois » du Calendrier. Déplacé au lot 5.1 depuis
    // `departementsAPI.update` : monté sur la route des départements, il exigeait
    // d'être ADMINISTRATEUR du département — un enseignant peignait la matrice,
    // la voyait changer, et recevait un 403 silencieux.
    saveVisibiliteColonnes: (departementId, visibiliteColonnes) =>
        api.put(`/planning/visibilite-colonnes/${departementId}`, { visibiliteColonnes }),

    // ---- Versions restaurables (lot 5.3) ----
    // `filtresColonnes` est envoyé PAR LE NAVIGATEUR : la visibilité globale des
    // colonnes vit en localStorage (`vis_<dept>_<annee>`), le serveur ne la connaît
    // pas. La matrice par mois, elle, est en base et le serveur la lit lui-même.
    getVersions: (departementId, anneeId) =>
        api.get(`/planning/versions/${departementId}/${anneeId}`),
    saveVersion: (departementId, anneeId, libelle, filtresColonnes) =>
        api.post('/planning/versions', { departementId, anneeScolaireId: anneeId, libelle, filtresColonnes }),
    // `options.creerInstantane: false` = on ANNULE une restauration : pas de nouvel
    // instantané technique, sinon le bouton se proposerait à lui-même.
    restaurerVersion: (versionId, filtresColonnes, options = {}) =>
        api.post(`/planning/versions/${versionId}/restaurer`, { filtresColonnes, ...options }),
    supprimerVersion: (versionId) =>
        api.delete(`/planning/versions/${versionId}`),

    // Lot PA.1 — l'état des 4 étapes et les chiffres de la vignette de l'accueil.
    // Le département en QUERY : `requireModule` le lit avant le routeur.
    getAccueil: (departementId, anneeId) =>
        api.get('/planning/accueil', { params: { departementId, anneeId } }),

    // Lot PA.2 — préparer une année à partir d'une précédente (assistant en 3 écrans)
    getPreparation: (departementId, anneeId) =>
        api.get('/planning/preparation', { params: { departementId, anneeId } }),
    apercuPreparation: (departementId, anneeId, options, semaines) =>
        api.post('/planning/preparation/apercu', { departementId, anneeId, options, semaines }),
    preparer: (departementId, anneeId, options, semaines) =>
        api.post('/planning/preparation', { departementId, anneeId, options, semaines }),
    marquerVerifiee: (preparationId, departementId) =>
        api.post('/planning/preparation/' + preparationId + '/verifiee', { departementId }),

    // Lot PA.3 — envoyer le planning (le PDF d'un modèle d'export) par courriel
    preparerEnvoi: (modeleId, departementId) =>
        api.post(`/planning/envois/${modeleId}/preparer`, { departementId }),
    lancerEnvoi: (modeleId, departementId, corps) =>
        api.post(`/planning/envois/${modeleId}/lancer`, { departementId, ...corps }),
    mailtoEnvoi: (modeleId, departementId, corps) =>
        api.post(`/planning/envois/${modeleId}/mailto`, { departementId, ...corps }),
    etatEnvoi: (departementId, tacheId) =>
        api.get(`/planning/envois/taches/${tacheId}`, { params: { departementId } }),
    // Les modèles de courriel (types pa_*) : la forme attendue par l'éditeur commun (AccordeonMail)
    modelesCourriel: (departementId) => api.get('/planning/modeles-courriel', { params: { departementId } }),
    listerVersionsMail: (typeMail, departementId) => api.get(`/planning/modeles-courriel/${typeMail}/versions`, { params: { departementId } }),
    enregistrerModeleMail: (typeMail, departementId, data) => api.put(`/planning/modeles-courriel/${typeMail}`, { departementId, ...data }),
    restaurerVersionMail: (typeMail, versionId, departementId) => api.post(`/planning/modeles-courriel/${typeMail}/restaurer/${versionId}`, { departementId })
};

export default api;

// ============================================
// API TABLEAU DE BORD DU PLANNING ANNUEL
//
// ⚠️ Le préfixe `/dashboard` est un nom historique : il ne s'agit PAS du tableau
// de bord de l'Accueil (celui-là est `accueilAPI`), mais du bloc
// « planifié vs objectif » en haut du Calendrier du Planning annuel.
//
// ⚠️ Lot 5.1 (2026-08-08) — `axios` BRUT au lieu de l'instance `api` : aucun
// en-tête `Authorization`, donc 401 depuis 0.B.2, avalé par un `catch` dans
// PlanningApp. Le bloc s'affichait VIDE en dev et en prod sans le moindre
// message. C'est le défaut que ce lot corrige en premier.
// ============================================

export const dashboardAPI = {
    getData: (departementId, anneeScolaireId) =>
        api.get(`/dashboard/${departementId}/${anneeScolaireId}`),

    // ✅ Lot 5.4 — les quatre fonctions `*SemestresConfig*` ont été retirées avec
    // les routes serveur qu'elles étaient seules à appeler. Confirmé avant retrait
    // (ce que le lot 5.2 avait mis en réserve) : le Paramétrage écrit
    // `semestres_heures_types` via `semestresHeuresAPI`, jamais `semestres_config`.
};

// ============================================
// API ACCUEIL DASHBOARD (nouveau tableau de bord)
// ============================================

export const accueilAPI = {
    getData: (departementId, anneeScolaireId) =>
        api.get(`/accueil/${departementId}/${anneeScolaireId}`)
};

export const semestresHeuresAPI = {
    getByDepartement: (deptId) =>
        api.get('/semestres-heures/' + deptId),
    saveBatch: (deptId, entries) =>
        api.post('/semestres-heures/' + deptId + '/batch', { entries }),
    deleteBySousNiveau: (deptId, sousNiveauId) =>
        api.delete('/semestres-heures/' + deptId + '/sous-niveau/' + sousNiveauId)
};

// ============================================
// API ENSEIGNANTS
// ============================================
export const enseignantsAPI = {
    getAll:     (departementId, anneeId) => api.get(`/enseignants?departementId=${departementId}&anneeId=${anneeId}`),
    getStatuts: ()              => api.get('/enseignants/statuts'),
    getById:    (id)            => api.get('/enseignants/' + id),
    create:     (data)          => api.post('/enseignants', data),
    update:     (id, data)      => api.put('/enseignants/' + id, data),
    delete:     (id, anneeId)   => api.delete(`/enseignants/${id}?anneeId=${anneeId}`),
    // 2026-10-10 (v74) : la photo de la PERSONNE de cette fiche (elle la suit d'une année sur l'autre).
    // ⚠️ L'instance pose `application/json` par défaut : le multipart doit être demandé explicitement.
    deposerPhoto: (id, departementId, fichier) => { const d = new FormData(); d.append('photo', fichier);
        return api.put(`/enseignants/${id}/photo?departementId=${departementId}`, d, { headers: { 'Content-Type': 'multipart/form-data' } }); },
    retirerPhoto: (id, departementId) => api.delete(`/enseignants/${id}/photo?departementId=${departementId}`),
};

// 2026-10-10 (v74) : « Mon espace » → MA photo (compte lié à une fiche enseignant).
export const monProfilAPI = {
    lire:    (anneeId) => api.get('/mon-profil', { params: { anneeId } }),
    deposerPhoto: (anneeId, fichier) => { const d = new FormData(); d.append('photo', fichier);
        return api.put(`/mon-profil/photo?anneeId=${anneeId}`, d, { headers: { 'Content-Type': 'multipart/form-data' } }); },
    retirerPhoto: (anneeId) => api.delete(`/mon-profil/photo?anneeId=${anneeId}`),
};

// ============================================
// API MODULE PROGRAMME
// ============================================
export const typesCourAPI = {
    // Catégories DURABLES par département (lot P.1, v100) : plus d'année.
    getAll:  (departementId)          => api.get(`/types-cours?departementId=${departementId}`),
    create:  (data)                   => api.post('/types-cours', data),
    update:  (id, data)               => api.put('/types-cours/' + id, data),
    delete:  (id, departementId)      => api.delete(`/types-cours/${id}?departementId=${departementId}`),
};

export const modalitesAPI = {
    getAll:  (departementId) => api.get('/modalites?departementId=' + departementId),
    create:  (data)          => api.post('/modalites', data),
    update:  (id, data)      => api.put('/modalites/' + id, data),
    delete:  (id, departementId)    => api.delete(`/modalites/${id}?departementId=${departementId}`),
    reorder: (ordre, departementId) => api.patch('/modalites/reorder', { departement_id: departementId, ordre }),
};

export const famillesOptionsAPI = {
    getAll:  (departementId, anneeId) => api.get(`/familles-options?departementId=${departementId}&anneeId=${anneeId}`),
    create:  (data)                   => api.post('/familles-options', data),
    update:  (id, data)               => api.put('/familles-options/' + id, data),
    delete:  (id, anneeId)            => api.delete(`/familles-options/${id}?anneeId=${anneeId}`),
};

export const coursAPI = {
    getAll:   (departementId, params) => api.get('/cours', { params: { departementId, ...params } }),
    getById:  (id)                    => api.get('/cours/' + id),
    create:   (data)                  => api.post('/cours', data),
    update:   (id, data)              => api.put('/cours/' + id, data),
    updatePN: (id, pnId)             => api.patch('/cours/' + id + '/pn', { programme_national_cours_id: pnId }),
    delete:   (id, anneeId)           => api.delete(`/cours/${id}?anneeId=${anneeId}`),
    reorder:  (ordre)                 => api.patch('/cours/reorder', { ordre }),
};

export const coursVolumesAPI = {
    getVolumes:       (coursId, anneeId)     => api.get('/cours-volumes', { params: { coursId, anneeId } }),
    saveVolume:       (data)                 => api.post('/cours-volumes', data),
    updateVolume:     (id, data)             => api.put('/cours-volumes/' + id, data),
    deleteVolume:     (id)                   => api.delete('/cours-volumes/' + id),
    getAffectations:  (coursId, anneeId)     => api.get('/cours-volumes/affectations', { params: { coursId, anneeId } }),
    addAffectation:   (data)                 => api.post('/cours-volumes/affectations', data),
    updateAffectation:(id, data)             => api.put('/cours-volumes/affectations/' + id, data),
    deleteAffectation:(id)                   => api.delete('/cours-volumes/affectations/' + id),
};

export const programmeNationalAPI = {
    getAll:       (departementId, params) => api.get('/programme-national', { params: { departementId, ...params } }),
    getById:      (id)                    => api.get('/programme-national/' + id),
    create:       (data)                  => api.post('/programme-national', data),
    update:       (id, data)              => api.put('/programme-national/' + id, data),
    delete:       (id)                    => api.delete('/programme-national/' + id),
    saveVolume:   (id, data)              => api.post('/programme-national/' + id + '/volumes', data),
    deleteVolume: (id, modaliteId)        => api.delete('/programme-national/' + id + '/volumes/' + modaliteId),
    reorder:      (ordre)                              => api.patch('/programme-national/reorder', { ordre }),
    comparaison:  (departementId, anneeId, semestreId) =>
        api.get('/programme-national/comparaison', { params: { departementId, anneeId, semestreId } }),
};

// ============================================
// API STATUTS ENSEIGNANTS (CRUD complet)
// ============================================
export const statutsAPI = {
    getAll:  ()              => api.get('/enseignants/statuts'),
    create:  (data)          => api.post('/enseignants/statuts', data),
    update:  (id, data)      => api.put('/enseignants/statuts/' + id, data),
    delete:  (id)            => api.delete('/enseignants/statuts/' + id),
};

// ============================================
// API SEMESTRES CONFIG
// ============================================
export const semestresConfigAPI = {
    getAll:    (departementId, anneeId) => api.get(`/semestres-config?departementId=${departementId}&anneeId=${anneeId}`),
    create:    (data)          => api.post('/semestres-config', data),
    update:    (id, data)      => api.put('/semestres-config/' + id, data),
    delete:    (id)            => api.delete('/semestres-config/' + id),
    reorder:   (ordre)         => api.patch('/semestres-config/reorder', { ordre }),
    cfPreview: (params)        => api.get('/semestres-config/carry-forward/preview', { params }),
    cfExecute: (data)          => api.post('/semestres-config/carry-forward', data),
};

export const sousNiveauxAPI = {
    getSousNiveaux: (deptId) => api.get('/niveaux/sous-niveaux/' + deptId),
    reorder:        (ordre)  => api.patch('/niveaux/sous-niveaux/reorder', { ordre }),
};

// ============================================
// API IMPORT BULK
// ============================================
export const importAPI = {
    importer: (type, departementId, anneeId, rows) =>
        api.post('/import/' + type, { departementId, anneeId, rows }),
};

// ============================================
// API CARRY FORWARD (récupération N-1 → N)
// ============================================
export const carryForwardAPI = {
    preview: (params) => api.get('/carry-forward/preview', { params }),
    execute: (data)   => api.post('/carry-forward', data),
};

// ============================================
// API UNITÉS D'ENSEIGNEMENT (maquette pédagogique)
// ============================================
export const ueAPI = {
    getAll:      (departementId, anneeId) => api.get('/ue', { params: { departementId, anneeId } }),
    getMaquette: (departementId, anneeId) => api.get('/ue/maquette', { params: { departementId, anneeId } }),
    create:      (data)         => api.post('/ue', data),
    update:      (id, data)     => api.put('/ue/' + id, data),
    delete:      (id)           => api.delete('/ue/' + id),
    linkPN:        (id, pnId, ordre) => api.post(`/ue/${id}/pn-cours`, { programme_national_cours_id: pnId, ordre: ordre || 0 }),
    unlinkPN:      (id, pnId)       => api.delete(`/ue/${id}/pn-cours/${pnId}`),
    reorder:         (ordre)               => api.patch('/ue/reorder', { ordre }),
    reorderPnCours:  (ueId, ordre)          => api.patch(`/ue/${ueId}/pn-cours/reorder`, { ordre }),
    saveCoursLocal:  (ueId, coursId, data)  => api.put(`/ue/${ueId}/cours-local/${coursId}`, data),
};

export const parcoursPnAPI = {
    getAll:       (departementId, anneeId) => api.get('/parcours-pn', { params: { departementId, anneeId } }),
    getBySemestre:(semestreConfigId)       => api.get('/parcours-pn/by-semestre', { params: { semestreConfigId } }),
    create:       (data)                   => api.post('/parcours-pn', data),
    update:       (id, data)               => api.put(`/parcours-pn/${id}`, data),
    delete:       (id)                     => api.delete(`/parcours-pn/${id}`),
    linkSemestre: (semestreConfigId, parcoursPnId) => api.post('/parcours-pn/semestre-link', { semestreConfigId, parcoursPnId }),
    unlinkSemestre:(semestreConfigId, parcoursPnId) => api.delete('/parcours-pn/semestre-link', { data: { semestreConfigId, parcoursPnId } }),
};

/**
 * MODULE SUIVI DE STAGE — lot 3.0 (le paramétrage).
 *
 * `getParametrage` rend TOUT l'onglet en un appel : référentiels, configurations,
 * contacts et modèles de courriel. Un aller-retour plutôt que cinq — l'écran
 * bascule ensuite entre ses trois sous-onglets sans réseau.
 *
 * ⚠️ Chaque écriture renvoie la LISTE À JOUR en plus de l'objet modifié. L'écran
 * n'a donc rien à recalculer de son côté : deux propriétaires du même tableau en
 * font toujours deux tableaux, et c'est le serveur qui a raison.
 */
/**
 * SUIVI DE STAGE — les pages PUBLIQUES (lot 3.2)
 *
 * ⚠️ UN CLIENT À PART, ET C'EST INDISPENSABLE. Le client `api` ci-dessus porte
 * deux comportements qui seraient désastreux ici :
 *   · il ajoute un en-tête `Authorization` s'il traîne un jeton dans le
 *     navigateur — l'étudiant qui remplit sa fiche sur le poste d'un enseignant
 *     connecté enverrait le jeton de celui-ci ;
 *   · surtout, **il vide le jeton et RECHARGE la page à tout 401**. Or
 *     l'identification publique répond 401 quand les quatre champs ne
 *     correspondent pas : la page se rechargerait en boucle, et l'étudiant
 *     n'aurait jamais le message lui disant quoi corriger.
 *
 * Ce client-ci n'a donc ni intercepteur de requête, ni intercepteur de réponse.
 */
const apiPublic = axios.create({
    baseURL: API_URL,
    headers: { 'Content-Type': 'application/json' }
});

/** Lot INV (v83) : l'accès aux comptes sans session — lien d'invitation / de réinitialisation, « mot de passe oublié ». */
export const comptesPublicAPI = {
    lireLien: (jeton) => apiPublic.get(`/public/comptes/lien/${encodeURIComponent(jeton)}`),
    definirMotDePasse: (jeton, motDePasse) => apiPublic.post(`/public/comptes/lien/${encodeURIComponent(jeton)}`, { mot_de_passe: motDePasse }),
    // `site_web` : le champ PIÈGE (invisible pour un humain) — toujours envoyé, vide pour une vraie personne.
    demanderLien: (email, siteWeb = '') => apiPublic.post('/public/comptes/oubli', { email, site_web: siteWeb })
};

export const stagePublicAPI = {
    // Porte COMMUNE — le lien envoyé à toute la promotion (écart E24).
    lireCampagne: (code) => apiPublic.get(`/public/stage/fiche-liaison/${code}`),
    identifier: (code, identification) =>
        apiPublic.post(`/public/stage/fiche-liaison/${code}/identifier`, identification),
    soumettreCommune: (code, corps) =>
        apiPublic.post(`/public/stage/fiche-liaison/${code}/soumettre`, corps),

    // Porte TOKENISÉE — les relances ciblées.
    // ⚠️ C'est cet appel qui horodate `jetons.ouvert_le`, la seule preuve
    // mesurée qu'un message est arrivé (écart E8).
    ouvrirTokenise: (token) => apiPublic.get(`/public/stage/etudiant/${token}`),
    soumettreTokenise: (token, corps) =>
        apiPublic.post(`/public/stage/etudiant/${token}/soumettre`, corps),

    // Porte MAÎTRE DE STAGE — étape ④ (lot 3.2 bis).
    // ⚠️ Aucun numéro étudiant n'est demandé ici : un maître de stage ne le
    // connaît pas (écart E56). C'est le jeton qui fait la porte.
    ouvrirMaitre: (token) => apiPublic.get(`/public/stage/maitre/${token}`),

    /**
     * L'adresse COURTE — une seule pour tous les jetons (écart E66).
     *
     * ⚠️ C'est le serveur qui dit de quel formulaire il s'agit (`porte`), parce
     * que c'est le JETON qui le sait. L'écran ne devine pas d'après l'adresse :
     * il n'y a plus de mauvaise page où se tromper.
     */
    ouvrirLien: (token) => apiPublic.get(`/public/stage/lien/${token}`),

    // Le PORTAIL DE DÉPÔT (lot 3.4 bis). ⚠️ L'identité voyage à CHAQUE appel :
    // aucune session n'est ouverte sur une page publique (E82).
    identifierDepot: (token, identite) => apiPublic.post(`/public/stage/depot/${token}/identifier`, identite),
    telechargerConvention: (token, identite) =>
        apiPublic.post(`/public/stage/depot/${token}/convention`, identite, { responseType: 'blob' }),
    deposerPiece: (token, attenduId, identite, fichier) => {
        const f = new FormData();
        // ⚠️ Les champs AVANT le fichier : `multer` remplit `req.body` dans l'ordre.
        f.append('numero', identite.numero);
        f.append('dateNaissance', identite.dateNaissance);
        f.append('fichier', fichier);
        return apiPublic.post(`/public/stage/depot/${token}/pieces/${attenduId}`, f,
                              { headers: { 'Content-Type': 'multipart/form-data' } });
    },
    envoyerDepot: (token, identite) => apiPublic.post(`/public/stage/depot/${token}/envoyer`, identite),
    // Lot C.2 — la page de dépôt de la CERTIFICATION. Même discipline : l'identité à chaque appel.
    identifierCertifDepot: (token, identite) => apiPublic.post(`/public/stage/certif-depot/${token}/identifier`, identite),
    deposerCertifPiece: (token, livrableId, identite, fichier) => {
        const f = new FormData();
        // ⚠️ Les champs AVANT le fichier : `multer` remplit `req.body` dans l'ordre.
        f.append('numero', identite.numero);
        f.append('dateNaissance', identite.dateNaissance);
        f.append('fichier', fichier);
        return apiPublic.post(`/public/stage/certif-depot/${token}/pieces/${livrableId}`, f,
                              { headers: { 'Content-Type': 'multipart/form-data' } });
    },
    // Lot C.3 — la page du MAÎTRE : pas d'identification, le jeton est la porte (E56).
    certifierMaitre: (token, corps) => apiPublic.post(`/public/stage/certif-attester/${token}/certifier`, corps),
    lireDocumentCertif: (token, depotId) =>
        apiPublic.get(`/public/stage/certif-attester/${token}/documents/${depotId}`, { responseType: 'blob' }),
    validerCertifDepot: (token, identite, declarations) =>
        apiPublic.post(`/public/stage/certif-depot/${token}/valider`, { ...identite, declarations }),
    soumettreMaitre: (token, corps) =>
        apiPublic.post(`/public/stage/maitre/${token}/soumettre`, corps),
    // La GRILLE D'ÉVALUATION (lot 3.6). ⚠️ Aucune identification : le jeton est la porte (E56).
    soumettreEvaluation: (token, corps) =>
        apiPublic.post(`/public/stage/evaluation/${token}/soumettre`, corps),
    // Lot 3.5c (E146) — la FICHE DE VISITE par le lien du tuteur. Le jeton est la porte ; `remplacer: true` modifie.
    enregistrerVisite: (token, corps) =>
        apiPublic.post(`/public/stage/visite/${token}/enregistrer`, corps)
};

/**
 * Le socle documents (lot 3.4) — la route de lecture UNIQUE.
 * ⚠️ En `blob` par l'instance authentifiée, jamais par un `<a href>` direct : le
 * navigateur n'enverrait pas le jeton, et le document resterait inaccessible SANS
 * la moindre erreur visible (piège mesuré au lot 1.5 d'Étudiants, sur les photos).
 */
// Lot N.1 — les notifications : le réglage du COMPTE CONNECTÉ (route hors /stage : la consultation suffit).
// Lot 3.5c-4 (E146, D5) — « Mes visites de stage » : la fiche de visite par le COMPTE du tuteur (route hors /enseignants).
export const mesVisitesAPI = {
    lister: (anneeId) => api.get('/mes-visites', { params: { anneeId } }),
    lire: (dossierId) => api.get(`/mes-visites/${dossierId}`),
    enregistrer: (dossierId, corps) => api.post(`/mes-visites/${dossierId}`, corps)
};

export const notificationsAPI = {
    lire: (departementId) => api.get('/notifications/moi', { params: { departementId } }),
    ecrire: (departementId, data) => api.put('/notifications/moi', { departementId, ...data }),
    maintenant: (departementId) => api.post('/notifications/moi/maintenant', { departementId })
};

// Aide contextuelle, étape A3 (v76) : « Signaler un manque » et les lectures du compte (bulle, Quoi de neuf).
export const aideAPI = {
    signaler: (departementId, data) => api.post('/aide/signalements', { departementId, ...data }),
    lectures: (module) => api.get(`/aide/lectures/${module}`),
    ecrireLectures: (module, data) => api.put(`/aide/lectures/${module}`, data),
    // Administration → Aide (super-administrateur)
    signalements: (params) => api.get('/admin/aide-signalements', { params }),
    donnerSuite: (id, data) => api.put(`/admin/aide-signalements/${id}`, data),
    // v96 : la réponse au demandeur, relue dans la fenêtre « Répondre… » — { texte }
    repondreSignalement: (id, data) => api.post(`/admin/aide-signalements/${id}/reponse`, data),
    supprimerSignalement: (id) => api.delete(`/admin/aide-signalements/${id}`)
};

export const documentsAPI = {
    lire: (id, departementId) => api.get(`/documents/${id}`, { params: { departementId }, responseType: 'blob' }),
    // Lot D.1 — la conservation : le bandeau du super-admin, le bloc d'un panneau, poser / lever.
    echeance: () => api.get('/documents/echeance'),
    conservation: (departementId, objetTable, objetId) =>
        api.get('/documents/conservation', { params: { departementId, objetTable, objetId } }),
    changerConservation: (departementId, objetTable, objetId, motif, lever = false) =>
        api.post('/documents/conservation', { departementId, objetTable, objetId, motif, lever })
};

export const stageAPI = {
    // L2a (E170) : `configurationId` = la vignette d'un processus (ses courriels à lui).
    getParametrage: (departementId, anneeId, configurationId = null) =>
        api.get('/stage/parametrage', { params: { departementId, anneeId, ...(configurationId ? { configurationId } : {}) } }),
    // L2a (E170) : « Les documents » d'un processus (ses 4 modèles et les sources) et « Repartir d'un autre modèle ».
    documentsProcessus: (configurationId, departementId) =>
        api.get(`/stage/configurations/${configurationId}/documents`, { params: { departementId } }),
    repartirModele: (configurationId, famille, sourceId, departementId) =>
        api.post(`/stage/configurations/${configurationId}/documents/${famille}/repartir`, { departementId, source_id: sourceId }),

    /**
     * Le tableau de bord : la liste, ses filtres et les onze indicateurs, en un
     * appel. `filtres.visibilite` est un tableau de clés cochées (`['actif']`)
     * — c'est ce qui traverse proprement une URL ; omis, le serveur applique les
     * défauts de la spec.
     */
    // Lot L1a (E169) : l'accueil à vignettes, en une lecture (suivi, certification, outils).
    getAccueil: (departementId, anneeId) =>
        api.get('/stage/accueil', { params: { departementId, anneeId } }),
    // Lot L1b : rechercher un étudiant dans tout le module (barre du haut).
    rechercherEtudiant: (departementId, anneeId, q) =>
        api.get('/stage/recherche', { params: { departementId, anneeId, q } }),
    getDashboard: (departementId, anneeId, filtres = {}) =>
        api.get('/stage/dashboard', {
            params: {
                departementId, anneeId,
                // L3a (E171) : le processus (vignette, filtre « Processus »)
                configurationId: filtres.configurationId || undefined,
                sousNiveauId: filtres.sousNiveauId || undefined,
                semestreId:   filtres.semestreId || undefined,
                statut:       filtres.statut || undefined,
                tuteurId:     filtres.tuteurId || undefined,
                recherche:    filtres.recherche || undefined,
                // v75 : seulement les dossiers « Passé en alternance — à exclure ».
                aExclure:     filtres.aExclure ? '1' : undefined,
                visibilite:   Array.isArray(filtres.visibilite) ? filtres.visibilite.join(',') : undefined
            }
        }),

    getDossier: (dossierId, departementId) =>
        api.get(`/stage/dossiers/${dossierId}`, { params: { departementId } }),

    /**
     * Prépare le lien COMMUN pour toute la promotion (étape ①).
     *
     * ⚠️ La réponse sépare volontairement le `mailto:` (objet + corps, SANS
     * destinataires) et la liste des adresses : 59 adresses encodées font
     * 1 826 caractères, et Windows tronque une URL `mailto:` au-delà de ~2 048
     * — sans erreur. L'écran copie la liste, l'assistante la colle.
     */
    preparerLienPromotion: (departementId, configurationId, mode) =>
        api.post('/stage/envois/lien-promotion', { departementId, configurationId, ...(mode ? { mode } : {}) }),
    // Lot 3.S-c — l'envoi à la promotion par le serveur (tâche de fond)
    lancerPromotion: (departementId, data) => api.post('/stage/envois/serveur/promotion/lancer', { departementId, ...data }),
    etatPromotion: (departementId, tacheId) => api.get(`/stage/envois/serveur/promotion/${tacheId}`, { params: { departementId } }),
    mailtoPromotion: (departementId, data) => api.post('/stage/envois/serveur/promotion/mailto', { departementId, ...data }),
    diffusionPromotion: (departementId, data) => api.post('/stage/envois/serveur/promotion/diffusion', { departementId, ...data }),

    /** Relance ciblée d'un étudiant : un lien tokenisé, un destinataire. */
    preparerRelanceEtudiant: (dossierId, departementId) =>
        api.post(`/stage/dossiers/${dossierId}/relance-lien`, { departementId }),

    /**
     * La fiche part au maître de stage — étape ③ (lot 3.2 bis).
     *
     * ⚠️ UNE SEULE ROUTE pour l'envoi ET pour la relance : la spec dit que le
     * bouton « ↩ Relancer le maître » ouvre le MÊME message, seul le libellé
     * change. Deux appels auraient fini par diverger sur le jeton.
     */
    preparerEnvoiMaitre: (dossierId, departementId) =>
        api.post(`/stage/dossiers/${dossierId}/envoi-maitre`, { departementId }),
    // Lot 3.4 bis — l'envoi de la convention et l'étape ⑦.
    preparerEnvoiConvention: (dossierId, departementId) =>
        api.post(`/stage/dossiers/${dossierId}/envoi-convention`, { departementId }),
    preparerRelanceCorrection: (dossierId, departementId) =>
        api.post(`/stage/dossiers/${dossierId}/relance-correction`, { departementId }),
    deciderPiece: (dossierId, departementId, depotId, decision, motif) =>
        api.post(`/stage/dossiers/${dossierId}/depots/${depotId}/decision`, { departementId, decision, motif }),
    validerDocuments: (dossierId, departementId) =>
        api.post(`/stage/dossiers/${dossierId}/depots/valider`, { departementId }),
    renvoyerPieces: (dossierId, departementId) =>
        api.post(`/stage/dossiers/${dossierId}/depots/renvoyer`, { departementId }),
    // Lot 3.6 — la grille d'évaluation : envoi (et relance, même lien), puis renvoi (nouveau lien, E100).
    preparerEnvoiGrille: (dossierId, departementId) =>
        api.post(`/stage/dossiers/${dossierId}/envoi-grille`, { departementId }),
    renvoyerGrille: (dossierId, departementId, commentaire) =>
        api.post(`/stage/dossiers/${dossierId}/renvoyer-grille`, { departementId, commentaire }),
    // Lot 3.8a — les modèles : les quatre listes, et l'éditeur des familles à critères (eval, visite).
    listerModeles: (departementId) => api.get('/stage/modeles', { params: { departementId } }),
    lireModele: (famille, id, departementId) => api.get(`/stage/modeles/${famille}/${id}`, { params: { departementId } }),
    creerModele: (famille, departementId, modele) => api.post(`/stage/modeles/${famille}`, { ...modele, departementId }),
    modifierModele: (famille, id, departementId, modele) => api.put(`/stage/modeles/${famille}/${id}`, { ...modele, departementId }),
    dupliquerModele: (famille, id, departementId) => api.post(`/stage/modeles/${famille}/${id}/dupliquer`, { departementId }),
    supprimerModele: (famille, id, departementId) => api.delete(`/stage/modeles/${famille}/${id}`, { params: { departementId } }),

    /**
     * L'ÉTAPE ⑤ — la décision du département (lot 3.3).
     *
     * ⚠️ Les trois CAS sont trois routes distinctes, et non une route avec un
     * paramètre « décision ». Ils ne font pas la même chose, ne demandent pas
     * les mêmes garde-fous — le refus exige un motif, le renvoi fabrique un
     * jeton — et un paramètre les aurait fait se ressembler à l'écrit alors
     * qu'ils diffèrent à l'exécution.
     */
    validerFiche: (dossierId, departementId) =>
        api.post(`/stage/dossiers/${dossierId}/valider`, { departementId }),
    // Lot 3.S-b — la fenêtre d'envoi (envoi par le serveur)
    envoyerServeur: (departementId, data) => api.post('/stage/envois/serveur/envoyer', { departementId, ...data }),
    essaiServeur: (departementId, data) => api.post('/stage/envois/serveur/essai', { departementId, ...data }),
    mailtoServeur: (departementId, data) => api.post('/stage/envois/serveur/mailto', { departementId, ...data }),
    messageEnAttente: (dossierId, departementId) => api.post(`/stage/dossiers/${dossierId}/message-en-attente`, { departementId }),
    annuaireEnvoi: (departementId) => api.get('/stage/envois/annuaire', { params: { departementId } }),
    messageEnvoye: (envoiId, departementId) => api.get(`/stage/envois/${envoiId}`, { params: { departementId } }),
    apercuEnvoi: (type, configurationId, departementId) => api.get(`/stage/apercu/${type}/${configurationId}`, { params: { departementId } }),
    renvoyerAuMaitre: (dossierId, departementId, commentaire) =>
        api.post(`/stage/dossiers/${dossierId}/renvoyer-maitre`, { departementId, commentaire }),
    refuserStage: (dossierId, departementId, commentaire) =>
        api.post(`/stage/dossiers/${dossierId}/refuser`, { departementId, commentaire }),
    /** E173 (v85) — ③ la validation du responsable, sur la fiche de l'étudiant ; la cloche (fiches modifiées par le maître). */
    validerFicheEtudiant: (dossierId, departementId) =>
        api.post(`/stage/dossiers/${dossierId}/valider-fiche-etudiant`, { departementId }),
    renvoyerAEtudiant: (dossierId, departementId, commentaire) =>
        api.post(`/stage/dossiers/${dossierId}/renvoyer-etudiant`, { departementId, commentaire }),
    getAlertesMaitre: (departementId, anneeId) =>
        api.get('/stage/alertes-maitre', { params: { departementId, anneeId } }),

    /** L'exclusion du suivi actif, et son retour. */
    exclureDossier: (dossierId, departementId, motif, commentaire) =>
        api.post(`/stage/dossiers/${dossierId}/exclure`, { departementId, motif, commentaire }),
    reintegrerDossier: (dossierId, departementId) =>
        api.post(`/stage/dossiers/${dossierId}/reintegrer`, { departementId }),

    /** L'étape ⑥ — la convention (lot 3.4). ⚠️ Le département voyage dans l'URL :
        `requireModule` passe avant `multer`, le corps n'est pas encore lu. */
    /** v82 : la civilité du maître de stage (« Madame » / « Monsieur » / « » = non précisée), saisie dans le dossier. */
    saisirCiviliteMaitre: (dossierId, departementId, civilite) =>
        api.put(`/stage/dossiers/${dossierId}/civilite-maitre`, { departementId, civilite }),
    choisirModeConvention: (dossierId, departementId, mode) =>
        api.put(`/stage/dossiers/${dossierId}/mode-convention`, { departementId, mode }),
    televerserConvention: (dossierId, departementId, fichier) => {
        const f = new FormData();
        f.append('fichier', fichier);
        // ⚠️ L'instance pose « application/json » par défaut : sans cet en-tête, le
        // fichier part SANS être annoncé comme tel, et le serveur répond « aucun
        // fichier reçu ». Trouvé par le banc à l'image du lot 3.4 — le banc d'API,
        // lui, construisait sa requête sans passer par cette instance.
        return api.post(`/stage/dossiers/${dossierId}/convention`, f, {
            params: { departementId },
            headers: { 'Content-Type': 'multipart/form-data' }
        });
    },

    /** L'annuaire des entreprises (lot 3.7a). */
    getAnnuaire: (departementId) => api.get('/stage/annuaire', { params: { departementId } }),

    /** Les résultats d'évaluation de l'année (lot 3.6 bis). */
    getEvaluations: (departementId, anneeId) =>
        api.get('/stage/evaluations', { params: { departementId, anneeId } }),

    /** Le registre Alternance (lot 3.3 bis). */
    getAlternance: (departementId, anneeId, filtres = {}) =>
        api.get('/stage/alternance', {
            params: {
                departementId, anneeId,
                regime: filtres.regime || undefined,
                sousNiveauId: filtres.sousNiveauId || undefined,
                recherche: filtres.recherche || undefined
            }
        }),
    getLigneAlternance: (etudiantId, departementId, anneeId) =>
        api.get(`/stage/alternance/${etudiantId}`, { params: { departementId, anneeId } }),
    modifierLigneAlternance: (etudiantId, departementId, anneeId, valeurs) =>
        api.put(`/stage/alternance/${etudiantId}`, { departementId, anneeId, valeurs }),
    // Lot 3.5b-0 (E151) — reprendre de l'année précédente (apprentis).
    getRepriseAlternance: (departementId, anneeId) =>
        api.get('/stage/alternance/reprise', { params: { departementId, anneeId } }),
    reprendreAlternance: (departementId, anneeId, etudiants, avecTuteur) =>
        api.post('/stage/alternance/reprise', { departementId, anneeId, etudiants, avecTuteur }),
    // Lot 3.5a — les tuteurs pédagogiques (une seule donnée par étudiant, E142).
    getTuteurs: (departementId, anneeId) =>
        api.get('/stage/tuteurs', { params: { departementId, anneeId } }),
    affecterTuteur: (dossierId, departementId, tuteurId) =>
        api.put(`/stage/dossiers/${dossierId}/tuteur`, { departementId, tuteurId }),
    affecterTuteurAlternant: (etudiantId, departementId, anneeId, tuteurId) =>
        api.put(`/stage/alternance/${etudiantId}/tuteur`, { departementId, anneeId, tuteurId }),
    enregistrerReglagesTuteurs: (departementId, data) =>
        api.put('/stage/tuteurs/reglages', { departementId, ...data }),
    // Lot 3.5b — l'aide à la répartition (souhaités, génération, enregistrement, versions).
    getRepartition: (departementId, anneeId) =>
        api.get('/stage/tuteurs/repartition', { params: { departementId, anneeId } }),
    enregistrerSouhaits: (departementId, anneeId, data) =>
        api.put('/stage/tuteurs/souhaits', { departementId, anneeId, ...data }),
    proposerRepartition: (departementId, anneeId, data) =>
        api.post('/stage/tuteurs/repartition/proposer', { departementId, anneeId, ...data }),
    enregistrerRepartition: (departementId, anneeId, data) =>
        api.post('/stage/tuteurs/repartition/enregistrer', { departementId, anneeId, ...data }),
    getVersionsRepartition: (departementId, anneeId) =>
        api.get('/stage/tuteurs/repartition/versions', { params: { departementId, anneeId } }),
    getEcartVersionRepartition: (departementId, anneeId, id) =>
        api.get(`/stage/tuteurs/repartition/versions/${id}/ecart`, { params: { departementId, anneeId } }),
    revenirVersionRepartition: (departementId, anneeId, id) =>
        api.post(`/stage/tuteurs/repartition/versions/${id}/revenir`, { departementId, anneeId }),
    supprimerVersionRepartition: (departementId, anneeId, id) =>
        api.delete(`/stage/tuteurs/repartition/versions/${id}`, { params: { departementId, anneeId } }),
    // Lot 3.5b-4 (E154) — l'envoi GROUPÉ aux tuteurs (même fenêtre que la promotion).
    preparerGroupeTuteurs: (departementId, anneeId, corps) =>
        api.post('/stage/tuteurs/envois/groupe/preparer', { departementId, anneeId, ...corps }),
    lancerGroupeTuteurs: (departementId, anneeId, corps) =>
        api.post('/stage/tuteurs/envois/groupe/lancer', { departementId, anneeId, ...corps }),
    etatGroupeTuteurs: (departementId, anneeId, id) =>
        api.get(`/stage/tuteurs/envois/groupe/${id}`, { params: { departementId, anneeId } }),
    // Lot 3.5a-3 — les courriels des tuteurs (Information tuteur, Information du suivi).
    getParametrageTuteurs: (departementId) =>
        api.get('/stage/tuteurs/parametrage', { params: { departementId } }),
    // Lot 3.5d — transmettre au CFA (E148, E165) : réglages (CFA par défaut / par formation, colonnes) et modèle.
    getReglagesCfa: (departementId, anneeId) =>
        api.get('/stage/tuteurs/cfa/reglages', { params: { departementId, anneeId } }),
    enregistrerReglagesCfa: (departementId, anneeId, data) =>
        api.put('/stage/tuteurs/cfa/reglages', { departementId, anneeId, ...data }),
    // Lot 3.5d-3 : la fenêtre « Transmettre au CFA » (lignes, brouillon, envoyer | essai | mailto avec le fichier).
    getTransmissionCfa: (departementId, anneeId) =>
        api.get('/stage/tuteurs/cfa/transmission', { params: { departementId, anneeId } }),
    preparerTransmissionCfa: (departementId, anneeId, cfa, etudiants) =>
        api.post('/stage/tuteurs/cfa/preparer', { departementId, anneeId, cfa, etudiants }),
    acteTransmissionCfa: (acte, departementId, anneeId, corps) =>
        api.post(`/stage/tuteurs/cfa/${acte}`, { departementId, anneeId, ...corps }),
    preparerEnvoiTuteurs: (departementId, anneeId, demande) =>
        api.post('/stage/tuteurs/envois/preparer', { departementId, anneeId, ...demande }),
    acteEnvoiTuteurs: (acte, departementId, corps) =>
        api.post(`/stage/tuteurs/envois/${acte}`, { departementId, ...corps }),
    // Lot 3.5d-0 (E164) — « Écrire aux entreprises » (Annuaire) : la fenêtre commune, message libre.
    preparerEnvoiEntreprise: (departementId, cle) =>
        api.post('/stage/annuaire/envois/preparer', { departementId, cle }),
    acteEnvoiEntreprise: (acte, departementId, corps) =>
        api.post(`/stage/annuaire/envois/${acte}`, { departementId, ...corps }),
    preparerGroupeEntreprises: (departementId, cles) =>
        api.post('/stage/annuaire/envois/groupe/preparer', { departementId, cles }),
    lancerGroupeEntreprises: (departementId, corps) =>
        api.post('/stage/annuaire/envois/groupe/lancer', { departementId, ...corps }),
    etatGroupeEntreprises: (departementId, id) =>
        api.get(`/stage/annuaire/envois/groupe/${id}`, { params: { departementId } }),
    apercuImportAlternance: (departementId, anneeId, lignes) =>
        api.post('/stage/alternance/import/apercu', { departementId, anneeId, lignes }),
    importerAlternance: (departementId, anneeId, lignes, fichier) =>
        api.post('/stage/alternance/import', { departementId, anneeId, lignes, fichier }),

    creerConfiguration: (departementId, anneeId, data) =>
        api.post('/stage/configurations', { departementId, anneeId, ...data }),
    modifierConfiguration: (id, departementId, data) =>
        api.put(`/stage/configurations/${id}`, { departementId, ...data }),
    dupliquerConfiguration: (id, departementId, data) =>
        api.post(`/stage/configurations/${id}/dupliquer`, { departementId, ...data }),
    supprimerConfiguration: (id, departementId) =>
        api.delete(`/stage/configurations/${id}`, { params: { departementId } }),
    recupererN1: (departementId, anneeId) =>
        api.post('/stage/configurations/recuperer-n1', { departementId, anneeId }),

    // Lot C.0 — Certification : le paramétrage des processus. Chaque écriture
    // renvoie la liste à jour (`processus`) : l'écran la pose sans relire.
    // L2b (E170) : `processusId` = la vignette d'un processus (ses textes et ses courriels à lui).
    getCertifParametrage: (departementId, anneeId, processusId = null) =>
        api.get('/stage/certif/parametrage', { params: { departementId, anneeId, ...(processusId ? { processusId } : {}) } }),
    creerProcessusCertif: (departementId, anneeId, data) =>
        api.post('/stage/certif/processus', { departementId, anneeId, ...data }),
    modifierProcessusCertif: (id, departementId, data) =>
        api.put(`/stage/certif/processus/${id}`, { departementId, ...data }),
    supprimerProcessusCertif: (id, departementId) =>
        api.delete(`/stage/certif/processus/${id}`, { params: { departementId } }),
    reprendreProcessusCertifN1: (departementId, anneeId) =>
        api.post('/stage/certif/processus/reprendre-n1', { departementId, anneeId }),
    // Lot C.0 bis : Paramétrage → Attestations. Les 3 modèles de courriel passent par
    // enregistrerModeleMail / restaurerVersionMail / listerVersionsMail, comme Contacts.
    enregistrerReglagesCertif: (departementId, data, processusId = null) =>
        api.put('/stage/certif/reglages', { departementId, ...data, ...(processusId ? { processusId } : {}) }),
    // Lot C.1 — Certification : le tableau de bord (lecture), le panneau, le contrôle aléatoire, le compteur.
    getCertifTableau: (departementId, anneeId, processusId) =>
        api.get('/stage/certif/tableau', { params: { departementId, anneeId, processusId: processusId || undefined } }),
    getCertifDossier: (id, departementId) =>
        api.get(`/stage/certif/dossiers/${id}`, { params: { departementId } }),
    controlerDeclarationCertif: (id, departementId, data) =>
        api.post(`/stage/certif/dossiers/${id}/controles`, { departementId, ...data }),
    getCertifCompteur: (departementId, anneeId) =>
        api.get('/stage/certif/compteur', { params: { departementId, anneeId } }),
    // Lot C.4a — les envois de la Certification (SMTP seulement, C31).
    brouillonEnvoiCertif: (departementId, type, dossierId) =>
        api.get('/stage/certif/envois/brouillon', { params: { departementId, type, dossierId } }),
    envoyerCertif: (departementId, data) => api.post('/stage/certif/envois/envoyer', { departementId, ...data }),
    essaiCertif: (departementId, data) => api.post('/stage/certif/envois/essai', { departementId, ...data }),
    brouillonLancementCertif: (departementId, processusId) =>
        api.get(`/stage/certif/processus/${processusId}/lancement`, { params: { departementId } }),
    lancerCertif: (departementId, processusId, data) =>
        api.post(`/stage/certif/processus/${processusId}/lancement`, { departementId, ...data }),
    etatLancementCertif: (departementId, tacheId) =>
        api.get(`/stage/certif/lancement/${tacheId}`, { params: { departementId } }),
    // Lot C.4b — recommencer un dossier (C21), saisir son contact (CR8), l'export (CR13).
    recommencerDossierCertif: (id, departementId, motif) =>
        api.post(`/stage/certif/dossiers/${id}/recommencer`, { departementId, motif }),
    saisirCertificateurCertif: (id, departementId, data) =>
        api.put(`/stage/certif/dossiers/${id}/certificateur`, { departementId, ...data }),
    exportCertif: (departementId, processusId) =>
        api.get('/stage/certif/export', { params: { departementId, processusId } }),

    enregistrerContacts: (departementId, data) =>
        api.put('/stage/contacts', { departementId, ...data }),

    // Le corps porte aussi `commentaire` : l'auteur dit POURQUOI il a changé le
    // texte, et ce commentaire devient l'entrée d'historique. Un comparateur de
    // versions aurait coûté plus cher et n'aurait jamais dit le pourquoi.
    // L2a/L2b (E170) : `configurationId` = un processus de suivi, `certifProcessusId` = un processus de certification.
    enregistrerModeleMail: (typeMail, departementId, data, configurationId = null, certifProcessusId = null) =>
        api.put(`/stage/modeles-mail/${typeMail}`, { departementId, ...data, ...(configurationId ? { configurationId } : {}),
                                                     ...(certifProcessusId ? { certifProcessusId } : {}) }),

    // Lot MC (2026-10-28) — refaire le brouillon d'un envoi d'un dossier, SANS rejouer de décision (« Modèles de courriel… »).
    brouillonEnvoiDossier: (dossierId, departementId, type) =>
        api.post(`/stage/dossiers/${dossierId}/brouillon/${type}`, { departementId }),

    // Lot MC (2026-10-28) — le modèle QU'UN ENVOI UTILISE (copie du processus si elle existe, sinon le département),
    // avec sa portée : « Modèles de courriel… » des fenêtres d'envoi ouvre et écrit celui-là.
    modeleMailEnvoi: (typeMail, departementId, configurationId = null, certifProcessusId = null) =>
        api.get(`/stage/modeles-mail/${typeMail}`, { params: { departementId, ...(configurationId ? { configurationId } : {}),
                                                              ...(certifProcessusId ? { certifProcessusId } : {}) } }),

    // L'historique n'est lu qu'à l'ouverture de l'accordéon : six modèles fois
    // leurs versions n'ont aucune raison de voyager avec tout le paramétrage.
    listerVersionsMail: (typeMail, departementId, configurationId = null, certifProcessusId = null) =>
        api.get(`/stage/modeles-mail/${typeMail}/versions`, { params: { departementId, ...(configurationId ? { configurationId } : {}),
                                                                         ...(certifProcessusId ? { certifProcessusId } : {}) } }),

    // `versionId` vaut un identifiant, ou la chaîne `origine` pour le texte livré
    // avec l'application. ⚠️ Restaurer AJOUTE une version, n'en efface aucune.
    restaurerVersionMail: (typeMail, versionId, departementId, configurationId = null, certifProcessusId = null) =>
        api.post(`/stage/modeles-mail/${typeMail}/restaurer/${versionId}`, { departementId, ...(configurationId ? { configurationId } : {}),
                                                                            ...(certifProcessusId ? { certifProcessusId } : {}) }),
};

// ── Planning oraux, lot PO.0 (v77) — spec doc/modules/planning_oraux.md §0 ──
// Le TABLEAU d'accueil passe par /planning-oraux-tableau : un compte qui CONSULTE range quand même le sien (D2).
export const planningOrauxAPI = {
    liste: (departementId, anneeId) => api.get('/planning-oraux', { params: { departementId, anneeId } }),
    lire: (id, departementId) => api.get(`/planning-oraux/${id}`, { params: { departementId } }),
    creer: (departementId, anneeId, donnees) => api.post('/planning-oraux', { ...donnees, departementId, anneeId }),
    modifier: (id, departementId, donnees) => api.put(`/planning-oraux/${id}`, { ...donnees, departementId }),
    supprimer: (id, departementId) => api.delete(`/planning-oraux/${id}`, { params: { departementId } }),
    // PO.1 — les candidats (D10) : ce que les formations offrent, avant la création ou pour un planning ; l'import
    sources: (departementId, anneeId, formations) => api.get('/planning-oraux/sources', { params: { departementId, anneeId, formations: formations.join(',') } }),
    sourcesDuPlanning: (id, departementId) => api.get(`/planning-oraux/${id}/sources`, { params: { departementId } }),
    importer: (id, departementId, choix) => api.post(`/planning-oraux/${id}/import`, { ...choix, departementId }),
    // PO.2a — la grille : déplacer un passage (jour null = « À placer »), placement automatique ('libres') ou « Vider la grille » ('vider')
    placer: (id, departementId, passageId, corps) => api.put(`/planning-oraux/${id}/passages/${passageId}`, { ...corps, departementId }),
    placement: (id, departementId, corps) => api.post(`/planning-oraux/${id}/placement`, { ...corps, departementId }),
    // PO.7 (D31) — l'activité d'une salle (modèle « Plusieurs oraux le même jour »)
    activiteColonne: (id, departementId, colonneId, activite) => api.put(`/planning-oraux/${id}/colonnes/${colonneId}/activite`, { departementId, activite }),
    // PO.2b — les jurys : d'une salle, « à la journée », propre d'un passage (null = celui de la salle), membres extérieurs
    jurySalle: (id, departementId, colonneId, enseignants, role = null) => api.put(`/planning-oraux/${id}/jurys/salle/${colonneId}`, { departementId, enseignants, role }),
    juryJournee: (id, departementId, colonneId, jour, periode = 'journee') => api.post(`/planning-oraux/${id}/jurys/salle/${colonneId}/journee`, { departementId, jour, periode }),
    juryPassage: (id, departementId, creneauId, enseignants, role = null) => api.put(`/planning-oraux/${id}/jurys/passage/${creneauId}`, { departementId, enseignants, role }),
    ajouterExterne: (id, departementId, creneauId, membre) => api.post(`/planning-oraux/${id}/jurys/passage/${creneauId}/externes`, { ...membre, departementId }),
    retirerExterne: (id, departementId, juryId) => api.delete(`/planning-oraux/${id}/jurys/externes/${juryId}`, { params: { departementId } }),
    // PO.3 — les évaluations (BUT1) : la liste (onglet Évaluations), « Répartir les évaluations »
    evaluations: (id, departementId, evaluations, confirmer = false) => api.put(`/planning-oraux/${id}/evaluations`, { departementId, evaluations, confirmer }),
    repartir: (id, departementId) => api.post(`/planning-oraux/${id}/evaluations/repartir`, { departementId }),
    // PO.2c — les versions (§10)
    versions: (id, departementId) => api.get(`/planning-oraux/${id}/versions`, { params: { departementId } }),
    sauverVersion: (id, departementId, libelle) => api.post(`/planning-oraux/${id}/versions`, { departementId, libelle }),
    restaurerVersion: (id, departementId, versionId) => api.post(`/planning-oraux/${id}/versions/${versionId}/restaurer`, { departementId }),
    supprimerVersion: (id, departementId, versionId) => api.delete(`/planning-oraux/${id}/versions/${versionId}`, { params: { departementId } }),
    // PO.4 — les modèles d'export (D27) : des réglages communs au planning, jamais un fichier
    exports: (id, departementId) => api.get(`/planning-oraux/${id}/exports`, { params: { departementId } }),
    creerExport: (id, departementId, modele) => api.post(`/planning-oraux/${id}/exports`, { ...modele, departementId }),
    remplacerExport: (id, departementId, exportId, modele) => api.put(`/planning-oraux/${id}/exports/${exportId}`, { ...modele, departementId }),
    supprimerExport: (id, departementId, exportId) => api.delete(`/planning-oraux/${id}/exports/${exportId}`, { params: { departementId } }),
    // PO.4b-1 (D28) — les modèles de convocation, communs au département
    // PO.5a (v79) — les modèles de courriel des oraux (mêmes méthodes que stageAPI : l'éditeur AccordeonMail les appelle)
    modelesCourriel: (departementId) => api.get('/planning-oraux/modeles-courriel', { params: { departementId } }),
    listerVersionsMail: (typeMail, departementId) => api.get(`/planning-oraux/modeles-courriel/${typeMail}/versions`, { params: { departementId } }),
    enregistrerModeleMail: (typeMail, departementId, data) => api.put(`/planning-oraux/modeles-courriel/${typeMail}`, { departementId, ...data }),
    restaurerVersionMail: (typeMail, versionId, departementId) => api.post(`/planning-oraux/modeles-courriel/${typeMail}/restaurer/${versionId}`, { departementId }),
    envois: (id, departementId) => api.get(`/planning-oraux/${id}/envois`, { params: { departementId } }),
    // PO.5b — l'envoi des convocations (candidat | maitre | jury) : préparer, lancer (convocations PDF jointes), suivre, secours
    preparerEnvoi: (id, departementId, genre, modification = false) => api.post(`/planning-oraux/${id}/envois/preparer`, { departementId, genre, modification }),
    // PO.5c — « Prévenir les concernés » : qui a reçu quelque chose qui n'est plus vrai ; l'annulation (messagerie)
    concernes: (id, departementId) => api.get(`/planning-oraux/${id}/envois/concernes`, { params: { departementId } }),
    annulationEnvoi: (id, departementId, envoiId) => api.post(`/planning-oraux/${id}/envois/${envoiId}/annulation`, { departementId }),
    lancerEnvoi: (id, departementId, corps) => api.post(`/planning-oraux/${id}/envois/lancer`, { ...corps, departementId }),
    etatEnvoi: (id, departementId, tacheId) => api.get(`/planning-oraux/${id}/envois/taches/${tacheId}`, { params: { departementId } }),
    mailtoEnvoi: (id, departementId, corps) => api.post(`/planning-oraux/${id}/envois/mailto`, { ...corps, departementId }),
    // PO.5p — la participation des maîtres : l'onglet, « Saisir la réponse »
    participations: (id, departementId) => api.get(`/planning-oraux/${id}/participations`, { params: { departementId } }),
    saisirParticipation: (id, departementId, creneauId, corps) => api.put(`/planning-oraux/${id}/participations/${creneauId}`, { ...corps, departementId }),
    // PO.4b-2 — les jetons des QR de vérification (e<id> = l'étudiant du passage, m<id> = son maître)
    jetonsConvocation: (id, departementId, cles) => api.get(`/planning-oraux/${id}/convocations/jetons`, { params: { departementId, destinataires: cles.join(',') } }),
    modelesConvocation: (departementId) => api.get('/planning-oraux/modeles-convocation', { params: { departementId } }),
    enregistrerModeleConvocation: (departementId, type, modele) => api.put(`/planning-oraux/modeles-convocation/${type}`, { ...modele, departementId }),
    origineModeleConvocation: (departementId, type) => api.post(`/planning-oraux/modeles-convocation/${type}/origine`, { departementId }),
    versionsModeleConvocation: (departementId, type) => api.get(`/planning-oraux/modeles-convocation/${type}/versions`, { params: { departementId } }),
    restaurerModeleConvocation: (departementId, type, versionId) => api.post(`/planning-oraux/modeles-convocation/${type}/versions/${versionId}/restaurer`, { departementId }),
    formations: (departementId) => api.get('/planning-oraux/formations', { params: { departementId } }),
    // PO.6 — « Récupérer N-1 » : les plannings de l'an dernier, puis la reprise de leur configuration (sans candidats)
    sourcesN1: (departementId, anneeId) => api.get('/planning-oraux/n-1', { params: { departementId, anneeId } }),
    recupererN1: (departementId, anneeId, ids) => api.post('/planning-oraux/n-1', { departementId, anneeId, ids }),
    salles: (departementId, dates = [], exclure = null) =>
        api.get('/planning-oraux/salles', { params: { departementId, dates: dates.join(','), exclure: exclure || undefined } }),
    sallesAilleurs: (departementId, q) => api.get('/planning-oraux/salles/ailleurs', { params: { departementId, q } }),
    ajouterSalle: (departementId, nom) => api.post('/planning-oraux/salles', { departementId, nom }),
    associerSalle: (departementId, salleId) => api.post(`/planning-oraux/salles/${salleId}/associer`, { departementId }),
    modifierSalle: (departementId, salleId, donnees) => api.put(`/planning-oraux/salles/${salleId}`, { ...donnees, departementId }),
    retirerSalle: (departementId, salleId) => api.delete(`/planning-oraux/salles/${salleId}`, { params: { departementId } }),
    lireTableau: (departementId) => api.get('/planning-oraux-tableau', { params: { departementId } }),
    ecrireTableau: (departementId, contenu) => api.put('/planning-oraux-tableau', { departementId, contenu }),
};
